import EditOutlined from "@mui/icons-material/EditOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import { CoAppActionButton, CoAppCancelTextButton } from "@/components/global/styled/global.styled";

import { LastSavedText, RuleFooterContainer, RuleTitleContainer } from "./styled/rulefooter.styled";

type RuleFooterProps = {
	ruleName?: string;
	lastSavedAt?: Date | null;
	/** True when the canvas (nodes/edges) or persisted toolbox variables (simple, formula, DB single/multi row) differ from the last save. */
	hasUnsavedGraphChanges?: boolean;
	onSave: () => void;
	onClose: () => void;
	/** Opens the edit-rule modal (name, description, tags, platform, groups). */
	onEditRule?: () => void;
	isSaving?: boolean;
};

const formatLastSavedText = (lastSavedAt?: Date | null) => {
	if (!lastSavedAt) {
		return "Last saved at --";
	}

	const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
		dateStyle: "medium",
		timeStyle: "short",
	});

	return `Last saved at ${dateTimeFormatter.format(lastSavedAt)}`;
};

const RuleFooter = ({ ruleName, lastSavedAt, hasUnsavedGraphChanges = false, onSave, onClose, onEditRule, isSaving = false }: RuleFooterProps) => {
	const displayRuleName = ruleName?.trim() ? ruleName : "Untitled Rule";
	const lastSavedText = formatLastSavedText(lastSavedAt);

	// Show the full name on hover ONLY when the displayed text is actually
	// clipped (scrollWidth exceeds the visible clientWidth). Re-measured on
	// mount, on name change, and on resize.
	const nameRef = useRef<HTMLElement | null>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const measure = useCallback(() => {
		const el = nameRef.current;
		if (!el) return;
		setIsTruncated(el.scrollWidth > el.clientWidth + 1);
	}, []);

	useLayoutEffect(() => {
		measure();
		const el = nameRef.current;
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (ro && el) ro.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			ro?.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [measure, displayRuleName]);

	return (
		<RuleFooterContainer>
			{/* LEFT — last-saved info (was on the right). */}
			<Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: "center" }}>
				<LastSavedText variant="body2">
					{lastSavedText}
				</LastSavedText>
				{hasUnsavedGraphChanges && (
					<Typography variant="body2" sx={{ color: "warning.main", fontWeight: 500 }}>
						Unsaved changes
					</Typography>
				)}
			</Stack>

			<RuleTitleContainer>
				<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center", minWidth: 0, width: "100%" }}>
					<Tooltip title={isTruncated ? displayRuleName : ""} arrow placement="top">
						<Typography
							ref={nameRef}
							variant="body1"
							sx={{
								fontWeight: 600,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								// minWidth:0 lets the text shrink (and thus truncate) inside
								// the flex row. `alignSelf: center` + `display: flex` keep the
								// text box vertically centered on the row's centerline even
								// though the row stretches to the (taller) pencil button — so
								// the name and the pencil sit on the exact same line.
								minWidth: 0,
								flex: "0 1 auto",
								lineHeight: 1,
								alignSelf: "center",
								display: "flex",
								alignItems: "center",
							}}
						>
							{displayRuleName}
						</Typography>
					</Tooltip>
					<IconButton
						size="small"
						onClick={onEditRule}
						disabled={!onEditRule}
						aria-label="Edit rule details"
						title={onEditRule ? "Edit rule details" : undefined}
						sx={{ flexShrink: 0, p: 0.5, opacity: 0.6, "&:hover": { opacity: 1 } }}
					>
						<EditOutlined fontSize="small" />
					</IconButton>
				</Stack>
			</RuleTitleContainer>

			{/* RIGHT — Close (tertiary) then Save Rule (primary). Standard
			    desktop convention: dismiss on left, primary on right. */}
			<Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, marginLeft: "auto", alignItems: "center" }}>
				<CoAppCancelTextButton
					onClick={onClose}
					sx={{ minWidth: 120, borderRadius: 2, marginRight: 0 }}
				>
					Close
				</CoAppCancelTextButton>
				<CoAppActionButton
					data-onboarding="save"
					onClick={onSave}
					disabled={isSaving}
					sx={{ minWidth: 140, borderRadius: 2, boxShadow: "none" }}
				>
					{isSaving ? "Saving..." : "Save Rule"}
				</CoAppActionButton>
			</Stack>
		</RuleFooterContainer>
	);
};

export default RuleFooter;
